import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    InputSignal,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ListIdtypeXResponse } from '@api/src/models/AgentApi/Contract/list-idtype-x-response';
import { SellerSettings } from '@api/src/models/AgentApi/Contract/seller-settings';
import { TranslatePipe } from '@ngx-translate/core';
import { AbstractFormComponent } from '@shared/components/form/abstract-form/abstract-form.component';
import { FormButton } from '@shared/components/form/form-button/form-button';
import { FormErrorComponent } from '@shared/components/form/form-error/form-error.component';
import { FormInputComponent } from '@shared/components/form/form-input/form-input.component';
import { FormSelectComponent } from '@shared/components/form/form-select/form-select.component';
import { SelectOption } from '@shared/components/form/form-select/select-option.model';
import { MediumType } from '@shared/data/medium-type';
import { monthList } from '@shared/data/months';
import { ClientSearchFormField, ClientSearchFormMode, ClientSearchFormService } from './client-search-form.service';

@Component({
    selector: 'app-client-search-form',
    imports: [
        FormButton,
        FormInputComponent,
        ReactiveFormsModule,
        TranslatePipe,
        FormErrorComponent,
        MatTabGroup,
        MatTab,
        FormSelectComponent,
    ],
    templateUrl: './client-search-form.html',
    styleUrl: './client-search-form.scss',
    providers: [ClientSearchFormService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'agent-client-search-form',
    },
})
export class ClientSearchForm extends AbstractFormComponent {
    @ViewChild(MatTabGroup) public tabGroup!: MatTabGroup;

    public readonly mediumTypes: InputSignal<SelectOption<ListIdtypeXResponse>[]> = input();
    public readonly sellerSettings: InputSignal<SellerSettings> = input();

    readonly #clientSearchFormService: ClientSearchFormService = inject(ClientSearchFormService);

    public override form: FormGroup = this.#clientSearchFormService.createReactiveForm();
    public readonly clientSearchFormField: typeof ClientSearchFormField = ClientSearchFormField;
    public readonly mediumType: typeof MediumType = MediumType;

    public readonly monthList: SelectOption[] = monthList;

    public override handlerSubmit: () => void = () => {
        this.handlers.handlerSubmit(
            this.#clientSearchFormService.getFormValueBySearchType(this.tabGroup.selectedIndex),
        );
    };

    constructor() {
        super();

        effect(() => {
            const sellerSettings = this.sellerSettings();

            if (sellerSettings) {
                this.#clientSearchFormService
                    .getControlByPath(ClientSearchFormField.PhoneCode)
                    .setValue(sellerSettings.defaultPhoneCountry);
            }
        });
    }

    public get mediumTypeValue(): MediumType {
        return this.#clientSearchFormService.getValueByPath(ClientSearchFormField.MediumType) as MediumType;
    }

    public changeSearchMode(value: ClientSearchFormMode): void {
        this.form.disable();

        switch (value) {
            case ClientSearchFormMode.Medium:
                this.#clientSearchFormService.getControlByPath(ClientSearchFormField.MediumType).enable();
                this.#clientSearchFormService.getControlByPath(ClientSearchFormField.MediumN).enable();
                break;
            case ClientSearchFormMode.MdiumSid:
                this.#clientSearchFormService.getControlByPath(ClientSearchFormField.MediumSid).enable();
                break;
            case ClientSearchFormMode.MobilePhone:
                this.#clientSearchFormService.getControlByPath(ClientSearchFormField.Mobnum).enable();
                this.#clientSearchFormService.getControlByPath(ClientSearchFormField.PhoneCode).enable();
                break;
            case ClientSearchFormMode.Email:
                this.#clientSearchFormService.getControlByPath(ClientSearchFormField.Email).enable();
                break;
        }

        this.form.patchValue(
            {
                [ClientSearchFormField.Email]: null,
                [ClientSearchFormField.Mobnum]: null,
                [ClientSearchFormField.MediumSid]: null,
                [ClientSearchFormField.MediumN]: null,
                [ClientSearchFormField.MediumType]: null,
                [ClientSearchFormField.ExpDateMonth]: null,
                [ClientSearchFormField.ExpDateYear]: null,
            },
            {
                emitEvent: false,
            },
        );
    }
}
